import React, {useEffect, useRef} from 'react';
import { ChatWindowComponentPaper, MessageContainer, ChatWindowAvatar, SpinnerImage, MessageWrapper, ImageWrapper } from './ChatWindowComponent.styled';
import Message from './Message';
import AnnAvatar from '../../images/AnnAvatar.svg';
import Spinner from '../icon/assets/spinners_chat.svg';
import Markdown from 'react-markdown';
import ShineThinking from '../ShineThinking';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import rehypeStringify from 'rehype-stringify'

const ChatWindowComponent = ({ messages, bottomRef, className, displayButtons = true, width, height, agentIsTyping, boxShadow, showImage, selectedAgent, backgroundColor }) => {
  // Filter out consecutive messages of the same type, keeping only the last received message
  let filteredMessages = [];
  let lastReceivedIndex = -1;
  const chatWindowRef = useRef(null);

  useEffect(() => {
    const handleLinkClick = (event) => {
      const link = event.target.closest('a');
      if (link && link.tagName === 'A' && link.target !== '_blank') {
        event.preventDefault();
        window.open(link.href, '_blank');
        event.stopPropagation();
      }
    };

    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      chatWindow.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (chatWindow) {
        chatWindow.removeEventListener('click', handleLinkClick);
      }
    };
  }, []);

  messages.forEach((message, index) => {
    if (message.type !== 'received') {
      filteredMessages.push(message);
    } else {
      // Check if this received message is consecutive with the previous one
      if (index === 0 || messages[index - 1].type !== 'received') {
        lastReceivedIndex = filteredMessages.length;
        filteredMessages.push(message);
      } else {
        filteredMessages[lastReceivedIndex] = message;
      }
    }
  });

  const renderMessage = (message, isTyping) => {
    // Type validation to prevent rendering objects
    let messageContent = message;
    
    if (typeof messageContent !== 'string') {
      console.error('Message is not a string:', messageContent);
      // Try to extract string from object
      if (typeof messageContent === 'object' && messageContent !== null) {
        if (messageContent.message) messageContent = messageContent.message;
        else if (messageContent.content) messageContent = messageContent.content;
        else if (messageContent.text) messageContent = messageContent.text;
        else {
          return (
            <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
              Error rendering message: Invalid message format
            </div>
          );
        }
      } else {
        return <div style={{ color: 'red' }}>Unable to display message</div>;
      }
    }

    return (
      <div className={isTyping ? 'streaming-message' : ''}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkParse, [remarkRehype, {
            // Configure GFM options
            singleTilde: true,
            stringLength: true,
            tablePipeAlign: true,
            tableCellPadding: true
          }]]}
          components={{
            // Enhanced markdown components
            table: ({node, ...props}) => (
              <table className="gfm-table" {...props} />
            ),
            tr: ({node, ...props}) => (
              <tr className="gfm-tr" {...props} />
            ),
            th: ({node, ...props}) => (
              <th className="gfm-th" {...props} />
            ),
            td: ({node, ...props}) => (
              <td className="gfm-td" {...props} />
            ),
            a: ({node, ...props}) => (
              <a 
                className="gfm-link" 
                target="_blank" 
                rel="noopener noreferrer" 
                {...props} 
              />
            ),
            input: ({node, ...props}) => {
              if (props.type === 'checkbox') {
                return (
                  <input 
                    className="gfm-checkbox" 
                    {...props} 
                    readOnly 
                  />
                )
              }
              return <input {...props} />
            },
            // Add support for strikethrough
            del: ({node, ...props}) => (
              <del className="gfm-strikethrough" {...props} />
            ),
            // Add support for task lists
            li: ({node, className, ...props}) => {
              if (className?.includes('task-list-item')) {
                return <li className="gfm-task-list-item" {...props} />
              }
              return <li {...props} />
            }
          }}
        >
          {messageContent}
        </ReactMarkdown>
        {isTyping && (
          <span className="typing-cursor"></span>
        )}
      </div>
    )
  }

  return (
    <ChatWindowComponentPaper ref={chatWindowRef} elevation={3} className={className} $height={height} boxShadow={boxShadow} sx={{backgroundColor: backgroundColor || 'white'}}>
      {showImage && !messages.length && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <img alt="Concierge Agent" />
        </div>
      )}
      {filteredMessages.map((item, index) => (
        <Message 
          key={index} 
          messageItem={item} 
          displayButtons={displayButtons} 
          width={width} 
          selectedAgent={selectedAgent}
          isTyping={item.isTyping}
        />
      ))}

      {agentIsTyping && messages.type !== 'sent' && (
        <MessageContainer width={width}>
          <ChatWindowAvatar src={AnnAvatar} alt="Agent Avatar" />
          <MessageWrapper $flex>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </MessageWrapper>
        </MessageContainer>
      )}

      <div ref={bottomRef} />
    </ChatWindowComponentPaper>
  );
};

export default ChatWindowComponent;
